/**
 * @component CalculatorCore
 * @description Shared calculator component used by PreiskalkulatorPage (internal)
 *   and PreiskalkulatorPublic (public/standalone). Contains all calculation logic,
 *   config loading, input sections and result panel.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Calculator, FileDown, ExternalLink, Package, Boxes, Warehouse } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

// ── Preset values ──
const SKU_PRESETS = [1, 2, 3, 5, 7, 10, 15, 20, 30, 50];
const PICK_PRESETS = [1, 2, 5, 10, 15, 25, 50, 75, 100];
const WEIGHT_PRESETS = [
  { value: 1, label: '1 kg' },
  { value: 2, label: '2 kg' },
  { value: 3, label: '3 kg' },
  { value: 5, label: '5 kg' },
  { value: 10, label: '10 kg' },
  { value: 15, label: '15 kg' },
  { value: 20, label: '20 kg' },
  { value: 31.5, label: '31,5 kg' },
];

// ── Price formatter (German locale) ──
export function formatPrice(value) {
  if (value === null || value === undefined) return '\u20AC0,00';
  const num = typeof value === 'string' ? parseFloat(value.replace(',', '.').replace('\u20AC', '')) : value;
  if (isNaN(num)) return '\u20AC0,00';
  return '\u20AC' + num.toFixed(2).replace('.', ',');
}

// ── Preset Button ──
function PresetBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 min-w-0 py-1 rounded-[8px] text-[12px] font-medium text-center transition-all ${
        active
          ? 'bg-white text-[var(--text-primary)] shadow-sm'
          : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
      }`}
    >
      {children}
    </button>
  );
}

// ── Slider with presets ──
function SliderGroup({ label, value, min, max, step, presets, formatValue, onChange }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="mb-5">
      <div className="flex justify-between items-center mb-2">
        <span className="text-[13px] font-medium text-[var(--text-primary)]">{label}</span>
        <span className="text-[13px] font-semibold text-[var(--primary)]">{formatValue(value)}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${pct}%, var(--border-card) ${pct}%, var(--border-card) 100%)`,
        }}
      />
      <div className="flex gap-0.5 mt-2 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px]">
        {presets.map(p => {
          const val = typeof p === 'object' ? p.value : p;
          const lbl = typeof p === 'object' ? p.label : String(p);
          return (
            <PresetBtn key={val} active={value === val} onClick={() => onChange(val)}>
              {lbl}
            </PresetBtn>
          );
        })}
      </div>
    </div>
  );
}

// ── Price row ──
function PriceRow({ label, detail, value }) {
  return (
    <div className="flex justify-between items-center py-1">
      <div>
        <span className="text-[12px] text-[var(--text-primary)]">{label}</span>
        {detail && <span className="text-[11px] text-[var(--text-tertiary)] ml-1.5">{detail}</span>}
      </div>
      <span className="text-[13px] font-semibold text-[var(--text-primary)] tabular-nums">{formatPrice(value)}</span>
    </div>
  );
}

/**
 * @param {Object} props
 * @param {'internal'|'public'} props.mode - Display mode
 * @param {function} [props.onResultChange] - Called with (result, selections) when calculation updates
 * @param {function} [props.renderActions] - Custom render for action buttons below result
 */
export default function CalculatorCore({ mode = 'internal', onResultChange, renderActions }) {
  const { t } = useLanguage();

  // ── Config state ──
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Selection state ──
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [skus, setSkus] = useState(1);
  const [picks, setPicks] = useState(1);
  const [weight, setWeight] = useState(1);
  const [selectedPackaging, setSelectedPackaging] = useState(null);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [result, setResult] = useState(null);

  const debounceRef = useRef(null);
  const countryRef = useRef(null);

  // ── Build selections object ──
  const getSelections = useCallback(() => ({
    warehouse: selectedWarehouse,
    country: selectedCountry,
    carrier: selectedCarrier,
    tier: selectedTier,
    skus, picks, weight,
    packaging: selectedPackaging,
    extras: selectedExtras,
  }), [selectedWarehouse, selectedCountry, selectedCarrier, selectedTier, skus, picks, weight, selectedPackaging, selectedExtras]);

  // ── Load config ──
  useEffect(() => {
    fetch(`${API}/api/public/calculator-config`)
      .then(r => r.json())
      .then(data => {
        setConfig(data);
        if (data.warehouses?.length) setSelectedWarehouse(data.warehouses[0]);
        if (data.countries?.length) setSelectedCountry(data.countries[0]);
        if (data.volumeTiers?.length) setSelectedTier(data.volumeTiers[0]);
        if (data.packagingMaterials?.length) {
          const medium = data.packagingMaterials.find(p => p.code === 'M');
          setSelectedPackaging(medium || data.packagingMaterials[0]);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // ── Load carriers when route changes ──
  useEffect(() => {
    if (!selectedWarehouse || !selectedCountry) return;
    fetch(`${API}/api/public/calculator-routes?warehouseCode=${selectedWarehouse.code}&countryCode=${selectedCountry.code}`)
      .then(r => r.json())
      .then(routes => {
        const seen = {};
        const unique = [];
        routes.forEach(r => {
          if (!seen[r.carrier_code]) {
            seen[r.carrier_code] = true;
            unique.push({
              code: r.carrier_code,
              name: r.carrier_name,
              badge: r.badge_text || r.carrier_code,
              badgeColor: r.badge_color || '#338edc',
              badgeTextColor: r.badge_text_color || '#fff',
            });
          }
        });
        setCarriers(unique);
        if (unique.length) setSelectedCarrier(unique[0]);
        else setSelectedCarrier(null);
      })
      .catch(() => { setCarriers([]); setSelectedCarrier(null); });
  }, [selectedWarehouse, selectedCountry]);

  // ── Calculate (debounced) ──
  const doCalculate = useCallback(() => {
    if (!selectedWarehouse || !selectedCountry || !selectedTier || !selectedPackaging) return;
    const monthlyPkgs = Math.round((selectedTier.min_packages + selectedTier.max_packages) / 2);
    fetch(`${API}/api/public/calculate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        warehouseCode: selectedWarehouse.code,
        countryCode: selectedCountry.code,
        monthlyPackages: monthlyPkgs,
        weight,
        skus,
        picks,
        packagingCode: selectedPackaging.code,
        extras: selectedExtras,
      }),
    })
      .then(r => r.json())
      .then(setResult)
      .catch(() => {});
  }, [selectedWarehouse, selectedCountry, selectedTier, weight, skus, picks, selectedPackaging, selectedExtras]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(doCalculate, 150);
    return () => clearTimeout(debounceRef.current);
  }, [doCalculate]);

  // ── Notify parent of result changes ──
  useEffect(() => {
    if (onResultChange && result) {
      onResultChange(result, getSelections());
    }
  }, [result, onResultChange, getSelections]);

  // ── Close country dropdown on outside click ──
  useEffect(() => {
    const handler = (e) => { if (countryRef.current && !countryRef.current.contains(e.target)) setShowCountryDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Toggle extra service ──
  const toggleExtra = (code) => {
    setSelectedExtras(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  // ── Get extra price for current tier ──
  const getExtraPrice = (svc) => {
    if (!svc.pricing || !selectedTier) return null;
    const match = svc.pricing.find(p => p.volumeTierId === selectedTier.id);
    return match ? match.price : null;
  };

  // ── PDF export ──
  const handlePdfExport = () => {
    const pdfData = {
      fromFlag: selectedWarehouse?.flag,
      fromCountry: selectedWarehouse?.name,
      toFlag: selectedCountry?.flag,
      toCountry: selectedCountry?.name,
      carrierCode: selectedCarrier?.code,
      carrierName: selectedCarrier?.name,
      carrierBadge: selectedCarrier?.badge,
      tierName: selectedTier?.name,
      tierRange: `${Number(selectedTier?.min_packages).toLocaleString('de-DE')} – ${Number(selectedTier?.max_packages).toLocaleString('de-DE')}`,
      weight: weight.toFixed(1).replace('.', ',') + ' kg',
      skus,
      picks,
      materialName: selectedPackaging?.name,
      materialDims: selectedPackaging?.dimensions,
      material: result?.material,
      handling: result?.handling,
      porto: result?.porto,
      extras: result?.extras,
      extrasTotal: result?.extrasTotal,
      totalPerPackage: result?.totalPerPackage,
      monthlyPackages: selectedTier ? Math.round((selectedTier.min_packages + selectedTier.max_packages) / 2) : 0,
      contactUrl: config?.settings?.contact_url || 'https://www.weship.eu/kontakt/',
    };
    sessionStorage.setItem('pdfData', JSON.stringify(pdfData));
    window.open('/calculator/pdf', '_blank');
  };

  // ── Filtered countries ──
  const filteredCountries = config?.countries?.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.toLowerCase().includes(countrySearch.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-2 border-[var(--primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  // Default actions if no custom renderActions provided
  const defaultActions = (
    <div className="mt-5 space-y-2">
      <button
        onClick={handlePdfExport}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-[8px] border border-[var(--border-card)] text-[13px] font-medium text-[var(--text-secondary)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
      >
        <FileDown className="w-4 h-4" />
        {t('calculator.savePdf')}
      </button>
      <a
        href={config?.settings?.contact_url || 'https://www.weship.eu/kontakt/'}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-[8px] bg-[var(--primary)] text-white text-[13px] font-medium hover:opacity-90 transition-opacity"
      >
        {t('calculator.contactExpert')}
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
      {/* ════════ LEFT COLUMN ════════ */}
      <div className="space-y-5">

        {/* ── 1. Validity + Categories ── */}
        <div className="border border-[var(--border-card)] rounded-[12px] bg-[var(--bg-card)] p-5">
          <p className="text-[13px] text-[var(--text-secondary)] mb-4">
            {t('calculator.validity')} <strong>5.000 {t('calculator.packagesPerMonth')}</strong>{' '}
            bzw. <strong>60.000 {t('calculator.packagesPerYear')}</strong>.
          </p>
          {config?.categories?.length > 0 && (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-[0.8px] text-[var(--text-tertiary)] mb-3">
                {t('calculator.productCategories')}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                {config.categories.map(cat => (
                  <div key={cat.id} className="flex items-center gap-2.5 px-3 py-2.5 border border-[var(--border-card)] rounded-[8px]">
                    <div
                      className="w-8 h-8 rounded-[6px] flex items-center justify-center text-[9px] font-bold text-white shrink-0"
                      style={{ background: cat.color || '#1E2D4F', fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {cat.code}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[12px] font-semibold text-[var(--text-primary)] truncate">{cat.name}</div>
                      {cat.description && (
                        <div className="text-[11px] text-[var(--text-tertiary)] truncate">{cat.description}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── 2. Shipping Route ── */}
        <div className="border border-[var(--border-card)] rounded-[12px] bg-[var(--bg-card)] p-5">
          <div className="text-[15px] font-semibold text-[var(--text-primary)] mb-4">{t('calculator.shippingRoute')}</div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4">
            {/* FROM */}
            <div className="flex-1 w-full">
              <div className="text-[11px] font-semibold uppercase tracking-[0.8px] text-[var(--text-tertiary)] mb-2">{t('calculator.shipFrom')}</div>
              <div className="flex gap-2">
                {config?.warehouses?.map(wh => (
                  <button
                    key={wh.id}
                    onClick={() => setSelectedWarehouse(wh)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-[8px] border-2 text-[13px] font-semibold transition-all ${
                      selectedWarehouse?.id === wh.id
                        ? 'border-[var(--primary)] bg-blue-50 dark:bg-blue-900/20 text-[var(--primary)]'
                        : 'border-[var(--border-card)] text-[var(--text-secondary)] hover:border-[var(--primary)]'
                    }`}
                  >
                    <span className="text-base">{wh.flag}</span> {wh.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Arrow */}
            <div className="hidden sm:flex items-center text-[var(--text-tertiary)] text-xl font-light mt-5">&rarr;</div>

            {/* TO */}
            <div className="flex-1 w-full" ref={countryRef}>
              <div className="text-[11px] font-semibold uppercase tracking-[0.8px] text-[var(--text-tertiary)] mb-2">{t('calculator.shipTo')}</div>
              <div className="relative">
                <input
                  type="text"
                  value={countrySearch}
                  onChange={e => { setCountrySearch(e.target.value); setShowCountryDropdown(true); }}
                  onFocus={() => setShowCountryDropdown(true)}
                  placeholder={t('calculator.searchCountry')}
                  className="w-full px-3 py-2.5 rounded-[8px] border border-[var(--border-card)] bg-[var(--bg-card)] text-[13px] focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 outline-none"
                />
                {showCountryDropdown && filteredCountries.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[8px] shadow-sm z-20 max-h-48 overflow-y-auto">
                    {filteredCountries.map(c => (
                      <button
                        key={c.id}
                        onClick={() => { setSelectedCountry(c); setCountrySearch(''); setShowCountryDropdown(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left hover:bg-[var(--bg-inset)] ${
                          selectedCountry?.id === c.id ? 'font-semibold text-[var(--primary)]' : 'text-[var(--text-primary)]'
                        }`}
                      >
                        <span>{c.flag}</span> {c.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Quick chips */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {config?.countries?.slice(0, 5).map(c => (
                  <button
                    key={c.id}
                    onClick={() => { setSelectedCountry(c); setCountrySearch(''); }}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] border transition-all ${
                      selectedCountry?.id === c.id
                        ? 'border-[var(--primary)] bg-blue-50 dark:bg-blue-900/20 text-[var(--primary)] font-medium'
                        : 'border-[var(--border-card)] text-[var(--text-secondary)] hover:border-[var(--primary)]'
                    }`}
                  >
                    <span className="text-sm">{c.flag}</span> {c.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Carrier options */}
          {carriers.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.8px] text-[var(--text-tertiary)] mb-2">{t('calculator.carrier')}</div>
              <div className="flex flex-wrap gap-2">
                {carriers.map(c => (
                  <button
                    key={c.code}
                    onClick={() => setSelectedCarrier(c)}
                    className={`flex items-center gap-2.5 px-4 py-2.5 rounded-[8px] border-2 text-[13px] font-semibold transition-all ${
                      selectedCarrier?.code === c.code
                        ? 'border-[var(--primary)] bg-blue-50 dark:bg-blue-900/20'
                        : 'border-[var(--border-card)] hover:border-[var(--primary)]'
                    }`}
                  >
                    <div
                      className="w-8 h-8 rounded-[6px] flex items-center justify-center text-[9px] font-bold shrink-0"
                      style={{ background: c.badgeColor, color: c.badgeTextColor, fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {c.badge}
                    </div>
                    <span className="text-[var(--text-primary)]">{c.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {selectedWarehouse && selectedCountry && carriers.length === 0 && (
            <div className="text-[13px] text-[var(--text-tertiary)] italic">{t('calculator.noCarriers')}</div>
          )}
        </div>

        {/* ── 3. Volume Tiers ── */}
        <div className="border border-[var(--border-card)] rounded-[12px] bg-[var(--bg-card)] p-5">
          <div className="text-[15px] font-semibold text-[var(--text-primary)] mb-4">{t('calculator.orderDetails')}</div>
          <div className="grid grid-cols-3 gap-3">
            {config?.volumeTiers?.map((tier, idx) => {
              const active = selectedTier?.id === tier.id;
              const TierIcon = [Package, Boxes, Warehouse][idx] || Package;
              const colors = { 0: '#6B7280', 1: '#338edc', 2: '#6366F1' };
              return (
                <button
                  key={tier.id}
                  onClick={() => setSelectedTier(tier)}
                  className={`p-4 rounded-[10px] border-2 text-center transition-all ${
                    active
                      ? 'border-[#1E2D4F] bg-[#1E2D4F] text-white'
                      : 'border-[var(--border-card)] hover:border-[var(--primary)] text-[var(--text-primary)]'
                  }`}
                >
                  <div className="flex justify-center mb-1">
                    <TierIcon size={24} style={{ color: active ? '#fff' : colors[idx] || '#6B7280' }} />
                  </div>
                  <div className="text-[14px] font-bold">{tier.name}</div>
                  <div className={`text-[12px] ${active ? 'text-white/80' : 'text-[var(--text-secondary)]'}`}>
                    {Number(tier.min_packages).toLocaleString('de-DE')} &ndash; {Number(tier.max_packages).toLocaleString('de-DE')}
                  </div>
                  <div className={`text-[11px] ${active ? 'text-white/60' : 'text-[var(--text-tertiary)]'}`}>
                    {t('calculator.packagesMonth')}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── 4. Article Details (Sliders) ── */}
        <div className="border border-[var(--border-card)] rounded-[12px] bg-[var(--bg-card)] p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.8px] text-[var(--text-tertiary)] mb-4">{t('calculator.articleDetails')}</div>
          <SliderGroup
            label={t('calculator.skus')} value={skus} min={1} max={50} step={1}
            presets={SKU_PRESETS}
            formatValue={v => String(Math.round(v))}
            onChange={setSkus}
          />
          <SliderGroup
            label={t('calculator.picks')} value={picks} min={1} max={100} step={1}
            presets={PICK_PRESETS}
            formatValue={v => String(Math.round(v))}
            onChange={setPicks}
          />
          <SliderGroup
            label={t('calculator.weightPerPackage')} value={weight} min={1} max={31.5} step={0.1}
            presets={WEIGHT_PRESETS}
            formatValue={v => v.toFixed(1).replace('.', ',') + ' kg'}
            onChange={setWeight}
          />
        </div>

        {/* ── 5. Packaging ── */}
        <div className="border border-[var(--border-card)] rounded-[12px] bg-[var(--bg-card)] p-5">
          <div className="text-[15px] font-semibold text-[var(--text-primary)] mb-4">{t('calculator.packaging')}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-5 gap-2.5">
            {config?.packagingMaterials?.map(pkg => {
              const active = selectedPackaging?.id === pkg.id;
              return (
                <button
                  key={pkg.id}
                  onClick={() => setSelectedPackaging(pkg)}
                  className={`p-3 rounded-[10px] border-2 text-center transition-all ${
                    active
                      ? 'border-[var(--primary)] bg-blue-50 dark:bg-blue-900/20'
                      : 'border-[var(--border-card)] hover:border-[var(--primary)]'
                  }`}
                >
                  <div
                    className="w-9 h-9 rounded-[8px] flex items-center justify-center text-[9px] font-bold text-white mx-auto mb-2"
                    style={{ background: pkg.color || '#2B6CB0', fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    {pkg.code}
                  </div>
                  <div className="text-[12px] font-semibold text-[var(--text-primary)] truncate">{pkg.name}</div>
                  {pkg.dimensions && (
                    <div className="text-[10px] text-[var(--text-tertiary)]">{pkg.dimensions}</div>
                  )}
                  <div className="text-[12px] font-bold text-[var(--text-primary)] mt-1">{formatPrice(pkg.price)}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── 6. Additional Services ── */}
        {config?.extraServices?.length > 0 && (
          <div className="border border-[var(--border-card)] rounded-[12px] bg-[var(--bg-card)] p-5">
            <div className="text-[15px] font-semibold text-[var(--text-primary)] mb-4">{t('calculator.extras')}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {config.extraServices.map(svc => {
                const active = selectedExtras.includes(svc.code);
                const price = getExtraPrice(svc);
                return (
                  <button
                    key={svc.id}
                    onClick={() => toggleExtra(svc.code)}
                    className={`relative p-3.5 rounded-[10px] border-2 text-center transition-all ${
                      active
                        ? 'border-[var(--primary)] bg-blue-50 dark:bg-blue-900/20'
                        : 'border-[var(--border-card)] hover:border-[var(--primary)]'
                    }`}
                  >
                    <div className={`absolute top-2 right-2 w-5 h-5 rounded-full bg-[var(--primary)] text-white flex items-center justify-center text-[10px] transition-all ${
                      active ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
                    }`}>
                      &#10003;
                    </div>
                    <div
                      className="w-9 h-9 rounded-[8px] flex items-center justify-center text-[9px] font-bold text-white mx-auto mb-2"
                      style={{ background: svc.color || '#338edc', fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {svc.code}
                    </div>
                    <div className="text-[12px] font-semibold text-[var(--text-primary)]">{svc.name}</div>
                    {svc.description && (
                      <div className="text-[10px] text-[var(--text-tertiary)]">{svc.description}</div>
                    )}
                    {price !== null && (
                      <div className="text-[12px] font-bold text-[var(--primary)] mt-1">{formatPrice(price)}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ════════ RIGHT COLUMN — Result ════════ */}
      <div className="lg:sticky lg:top-6 self-start">
        <div className="border border-[var(--primary)]/30 rounded-[12px] bg-[var(--bg-card)] p-5">
          <div className="text-[11px] font-semibold uppercase tracking-[0.8px] text-[var(--text-tertiary)] mb-4">
            {t('calculator.result')}
          </div>

          {/* Carrier */}
          {selectedCarrier && (
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-[var(--border-card)]">
              <div
                className="w-10 h-10 rounded-[8px] flex items-center justify-center text-[10px] font-bold shrink-0"
                style={{ background: selectedCarrier.badgeColor, color: selectedCarrier.badgeTextColor, fontFamily: "'JetBrains Mono', monospace" }}
              >
                {selectedCarrier.badge}
              </div>
              <div>
                <div className="text-[14px] font-semibold text-[var(--text-primary)]">{selectedCarrier.name}</div>
                <div className="text-[12px] text-[var(--text-tertiary)]">
                  {t('calculator.standardShipping')} {selectedCountry?.name || ''}
                </div>
              </div>
            </div>
          )}

          {result ? (
            <>
              {/* Handling Fees */}
              <div className="mb-4">
                <div className="text-[11px] font-bold uppercase tracking-[1px] text-[var(--text-secondary)] mb-2 pb-1.5 border-b border-[var(--border-card)]">
                  {t('calculator.handlingFees')}
                </div>
                <div className="space-y-1.5">
                  <PriceRow label={t('calculator.orderProcessing')} detail="p.P." value={result.handling?.orderProcessing} />
                  <PriceRow label={t('calculator.picking')} detail={`${picks} Picks`} value={result.handling?.picking} />
                  <PriceRow label={t('calculator.commission')} detail={`${skus} SKUs`} value={result.handling?.commission} />
                  <PriceRow label={t('calculator.packagingFee')} detail={`${t('calculator.upToKg')} ${weight.toFixed(1).replace('.', ',')} kg`} value={result.handling?.packaging} />
                </div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-[var(--border-card)]">
                  <span className="text-[12px] font-bold text-[var(--text-primary)]">{t('calculator.handlingTotal')}</span>
                  <span className="text-[13px] font-bold text-[var(--text-primary)]">{formatPrice(result.handling?.total)}</span>
                </div>
              </div>

              {/* Material */}
              <div className="mb-4">
                <div className="text-[11px] font-bold uppercase tracking-[1px] text-[var(--text-secondary)] mb-2 pb-1.5 border-b border-[var(--border-card)]">
                  {t('calculator.materialCosts')}
                </div>
                <PriceRow label={selectedPackaging?.name || ''} detail={selectedPackaging?.dimensions || ''} value={result.material} />
              </div>

              {/* Porto */}
              <div className="mb-4">
                <div className="text-[11px] font-bold uppercase tracking-[1px] text-[var(--text-secondary)] mb-2 pb-1.5 border-b border-[var(--border-card)]">
                  {t('calculator.shippingCosts')}
                </div>
                <PriceRow label={t('calculator.porto')} detail={selectedCarrier?.badge || ''} value={result.porto} />
              </div>

              {/* Extras */}
              {result.extras?.length > 0 && (
                <div className="mb-4">
                  <div className="text-[11px] font-bold uppercase tracking-[1px] text-[var(--text-secondary)] mb-2 pb-1.5 border-b border-[var(--border-card)]">
                    {t('calculator.extras')}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {result.extras.map(e => (
                      <span key={e.code} className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 dark:bg-blue-900/20 text-[var(--primary)]">
                        {e.name} ({formatPrice(e.price)})
                      </span>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-1.5 border-t border-[var(--border-card)]">
                    <span className="text-[12px] font-bold text-[var(--text-primary)]">{t('calculator.extrasTotal')}</span>
                    <span className="text-[13px] font-bold text-[var(--text-primary)]">{formatPrice(result.extrasTotal)}</span>
                  </div>
                </div>
              )}

              {/* ── TOTAL ── */}
              <div className="mt-5 pt-4 border-t-2 border-[var(--border-card)] flex justify-between items-end">
                <div>
                  <div className="text-[14px] font-bold text-[var(--text-primary)]">{t('calculator.total')}</div>
                  <div className="text-[11px] text-[var(--text-tertiary)]">{t('calculator.perPackage')}</div>
                </div>
                <div className="text-[28px] font-bold text-[var(--text-primary)] leading-none">
                  <span className="text-[18px]">&euro;</span>{result.totalPerPackage?.toFixed(2).replace('.', ',')}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-[var(--text-tertiary)] text-[13px]">
              <Calculator className="w-8 h-8 mx-auto mb-2 opacity-30" />
              {t('calculator.validity')}...
            </div>
          )}

          {/* Action buttons */}
          {renderActions ? renderActions({ result, selections: getSelections(), handlePdfExport, config }) : defaultActions}
        </div>
      </div>
    </div>
  );
}
